import React, { useEffect, useMemo, useState } from "react";
import { Heart, Loader2, AlertCircle, Lock } from "lucide-react";
import { createDonationCheckout, fetchDonationConfig } from "../lib/api";

const FALLBACK_PRESETS = [
  { id: "10", amount: 10 },
  { id: "25", amount: 25 },
  { id: "50", amount: 50 },
  { id: "100", amount: 100 },
];
const FALLBACK_FREQS = [
  { id: "once", label: "One-time" },
  { id: "monthly", label: "Monthly" },
  { id: "quarterly", label: "Quarterly" },
  { id: "yearly", label: "Yearly" },
];
const FREQ_SUFFIX = { once: "", monthly: " / month", quarterly: " / quarter", yearly: " / year" };

const fmtMoney = (n) =>
  `$${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;

export const DonationForm = () => {
  const [config, setConfig] = useState(null);
  const [frequency, setFrequency] = useState("once");
  const [presetId, setPresetId] = useState("25");
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [showAmount, setShowAmount] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    fetchDonationConfig()
      .then((c) => alive && setConfig(c))
      .catch(() => alive && setConfig(null));
    return () => {
      alive = false;
    };
  }, []);

  const presets = config?.presets || FALLBACK_PRESETS;
  const frequencies = config?.frequencies || FALLBACK_FREQS;

  const usingCustom = custom !== "";
  const amount = useMemo(() => {
    if (usingCustom) {
      const n = parseFloat(custom);
      return Number.isFinite(n) ? n : null;
    }
    const p = presets.find((x) => x.id === presetId);
    return p ? p.amount : null;
  }, [usingCustom, custom, presetId, presets]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!amount || amount < 1) {
      setError("Please choose or enter an amount of at least $1.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        frequency,
        donor_name: name.trim() || undefined,
        donor_message: message.trim() || undefined,
        anonymous,
        show_amount: showAmount,
        origin_url: window.location.origin,
      };
      if (usingCustom) payload.custom_amount = Math.round(parseFloat(custom) * 100) / 100;
      else payload.amount_preset = presetId;
      const data = await createDonationCheckout(payload);
      if (data?.url) window.location.href = data.url;
      else throw new Error("No checkout URL received.");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not start checkout. Please try again.";
      setError(typeof msg === "string" ? msg : "Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  const cta = amount ? `${frequency === "once" ? "Donate" : "Give"} ${fmtMoney(amount)}${FREQ_SUFFIX[frequency]}` : "Choose an amount";

  return (
    <form onSubmit={onSubmit} data-testid="donation-form" noValidate style={{
      border: "1px solid hsl(var(--line))",
      background: "hsl(var(--bg-elev))",
      padding: "1.75rem",
      display: "flex",
      flexDirection: "column",
      gap: "1.5rem",
    }}>
      {/* Frequency */}
      <div>
        <Label>How often</Label>
        <div role="tablist" aria-label="Donation frequency" data-testid="donation-frequency" style={{
          display: "flex", flexWrap: "wrap", gap: 6,
          border: "1px solid hsl(var(--line))",
          background: "hsl(var(--bg))",
          padding: 4, borderRadius: 999,
        }}>
          {frequencies.map((f) => {
            const active = frequency === f.id;
            return (
              <button key={f.id} type="button" role="tab" aria-selected={active}
                data-testid={`freq-${f.id}`}
                onClick={() => setFrequency(f.id)}
                style={{
                  flex: "1 1 auto", minWidth: 78, padding: "0.5rem 0.75rem",
                  fontSize: "0.82rem", fontWeight: 500,
                  background: active ? "hsl(var(--accent))" : "transparent",
                  color: active ? "hsl(var(--accent-ink))" : "hsl(var(--ink))",
                  border: "none", borderRadius: 999, cursor: "pointer",
                  transition: "background-color 160ms ease, color 160ms ease",
                }}>
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Amount */}
      <div>
        <Label>Amount{frequency !== "once" ? <span style={{ color: "hsl(var(--muted))", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}> · billed {frequencies.find((f) => f.id === frequency)?.label?.toLowerCase()}</span> : null}</Label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          {presets.map((p) => {
            const active = !usingCustom && presetId === p.id;
            return (
              <button key={p.id} type="button" data-testid={`amount-${p.id}`}
                onClick={() => { setPresetId(p.id); setCustom(""); }}
                style={{
                  padding: "0.85rem 0.5rem",
                  fontFamily: "var(--font-display)",
                  fontSize: "1.15rem", fontWeight: 500,
                  background: active ? "hsl(var(--ink))" : "hsl(var(--bg))",
                  color: active ? "hsl(var(--bg-elev))" : "hsl(var(--ink))",
                  border: `1px solid ${active ? "hsl(var(--ink))" : "hsl(var(--line))"}`,
                  borderRadius: 2, cursor: "pointer",
                  transition: "background-color 160ms ease, color 160ms ease, border-color 160ms ease",
                }}>
                ${p.amount}
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: 10, position: "relative" }}>
          <span aria-hidden style={{
            position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            color: "hsl(var(--muted))", fontSize: "0.95rem",
          }}>$</span>
          <input
            type="number" inputMode="decimal" min="1" max="10000" step="1"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="Or enter a custom amount"
            data-testid="amount-custom"
            aria-label="Custom donation amount"
            style={{ ...inputStyle, paddingLeft: 26 }}
          />
        </div>
      </div>

      {/* Donor details */}
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Label as="span">Your name <Optional /></Label>
          <input type="text" maxLength={80} value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Shown on the supporter wall" style={inputStyle} data-testid="donor-name" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Label as="span">A short public message <Optional /></Label>
          <textarea maxLength={280} rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="Why this archive matters to you (optional)"
            style={{ ...inputStyle, resize: "vertical", minHeight: 78 }} data-testid="donor-message" />
        </label>
      </div>

      {/* Privacy choices */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Checkbox checked={anonymous} onChange={setAnonymous} testid="donor-anonymous">
          Give anonymously — list me as <em>“Anonymous Supporter.”</em>
        </Checkbox>
        <Checkbox checked={showAmount} onChange={setShowAmount} testid="donor-show-amount">
          Show the amount I gave on the supporter wall.
        </Checkbox>
      </div>

      {error && (
        <div role="alert" data-testid="donation-error" style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "0.7rem 0.9rem", fontSize: "0.9rem",
          border: "1px solid hsl(var(--destructive) / 0.5)",
          background: "hsl(var(--destructive) / 0.08)",
          color: "hsl(var(--destructive))",
        }}>
          <AlertCircle size={16} aria-hidden /> {error}
        </div>
      )}

      <button type="submit" className="btn btn--primary" disabled={submitting} data-testid="donate-submit"
        style={{ justifyContent: "center", fontSize: "1rem", padding: "1rem 1.4rem" }}>
        {submitting ? <Loader2 size={16} aria-hidden style={{ animation: "wnbf-spin 0.9s linear infinite" }} /> : <Heart size={16} aria-hidden />}
        {submitting ? "Redirecting to secure checkout…" : cta}
      </button>

      <p style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
        fontSize: "0.78rem", color: "hsl(var(--muted))", margin: 0,
      }}>
        <Lock size={12} aria-hidden /> Payments are processed securely by Stripe. We never see your card details.
      </p>
    </form>
  );
};

const Label = ({ children, as: As = "div" }) => (
  <As style={{
    display: "block",
    fontSize: "0.74rem", letterSpacing: "0.14em", textTransform: "uppercase",
    color: "hsl(var(--muted))", fontWeight: 600, marginBottom: 10,
  }}>{children}</As>
);

const Optional = () => (
  <span style={{ color: "hsl(var(--muted-2))", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>(optional)</span>
);

const Checkbox = ({ checked, onChange, children, testid }) => (
  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", fontSize: "0.9rem", color: "hsl(var(--ink-soft))" }}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid}
      style={{ marginTop: 3, width: 16, height: 16, accentColor: "hsl(var(--accent))", cursor: "pointer" }} />
    <span>{children}</span>
  </label>
);

const inputStyle = {
  width: "100%",
  padding: "0.7rem 0.85rem",
  border: "1px solid hsl(var(--line))",
  background: "hsl(var(--bg))",
  color: "hsl(var(--ink))",
  fontSize: "0.95rem",
  fontFamily: "var(--font-body)",
  borderRadius: 2,
};
